import { FolderResult } from "../../redux/interfaces/folder.types.ts";
import {
  setFilterState,
  setIsLoading,
} from "../../redux/slices/folderSlice.ts";
import api from "../../api";
import dayjs from "dayjs";
import { FilterDates, FilterUser } from "../../redux/interfaces/folder.types";

interface Params {
  filter: string;
  sort: string;
  date: { startDate: string; endDate: string } | null;
  user: { email: string; group: string } | null;
  search: string | null;
}

export type FolderFilterAction =
  | ReturnType<typeof setFilterState>
  | ReturnType<typeof setIsLoading>;

export type FolderFilterDispatch = (
  action: FolderFilterAction,
) => FolderFilterAction;

const filterOptions: any = {
  all: {
    is_active: true,
  },
  to_review: {
    is_active: true,
    requirement_status: "pending_review",
  },
  pending: {
    is_active: true,
    no_pending_uploads: true,
    is_multireport_delivered: false,
    is_lease_contract_generated: false,
  },
  disabled: {
    is_active: false,
  },
  multireport_delivered: {
    is_active: true,
    is_multireport_delivered: true,
  },
  is_ready_for_multireport: {
    is_active: true,
    is_ready_for_multireport: true,
    is_multireport_delivered: false,
  },
  multi_report_delivered_no_contract: {
    is_active: true,
    is_multireport_delivered: true,
    is_lease_contract_generated: false,
  },
  multireport_pending: {
    is_active: true,
    is_multireport_delivered: false,
  },
  lease_contract_generated: {
    is_active: true,
    is_lease_contract_generated: true,
  },
  lease_contract_pending: {
    is_active: true,
    is_lease_contract_generated: false,
  },
  cja_validated_filter: {
    cja_validated_filter: true,
  },
  cja_pending_review_filter: {
    cja_pending_review_filter: true,
  },
  filter_folders_with_oldest_pending_review: {
    filter_folders_with_oldest_pending_review: true,
  },
};

const sortOptions: any = {
  default: {
    ordering: null,
  },
  recent: {
    ordering: "-created_at",
  },
  oldest: {
    ordering: "created_at",
  },
  recent_loaded: {
    ordering: "-last_requirement_update",
  },
};

const performFilterByCriteria = async (url: string, isPending: boolean) => {
  const folders = await api.get<FolderResult>(url);
  if (isPending) {
    const filteredResults = folders.data.results.filter((folder) => {
      if (!folder.templates || folder.templates.length === 0) {
        return false;
      }

      return folder.templates.some(
        (template) =>
          template.slug === "tenant-pf" || template.slug === "tenant-pm",
      );
    });

    return {
      ...folders.data,
      results: filteredResults,
      count: filteredResults.length,
    };
  }
  return folders.data;
};

export const getFilterQueryParams = async (params: Params) => {
  const savedFilter = sessionStorage.getItem("folderFilter");
  const savedSpecialFilter = sessionStorage.getItem("specialFilter");
  const savedSort = sessionStorage.getItem("folderSort");
  const isPending = savedFilter === "pending";

  let queryParams = {
    ...sortOptions[params.sort],
  };

  if (savedSort) {
    queryParams = {
      ...queryParams,
      ...sortOptions[savedSort],
    };
  }

  if (savedFilter) {
    queryParams = {
      ...queryParams,
      ...filterOptions[savedFilter],
    };
  }

  if (savedSpecialFilter && filterOptions[savedSpecialFilter]) {
    queryParams = {
      ...queryParams,
      ...filterOptions[savedSpecialFilter],
    };
  }

  if (params.filter && filterOptions[params.filter]) {
    queryParams = {
      ...queryParams,
      ...filterOptions[params.filter],
    };
  }

  if (params.date) {
    queryParams["created_at_after"] = params.date.startDate;
    queryParams["created_at_before"] = params.date.endDate;
  }
  if (params.search && params.search !== "")
    queryParams["search"] = params.search;

  if (params.user && params.user.group === "operations")
    queryParams["assigned_user_email"] = params.user.email;
  else if (params.user && params.user.group === "cja")
    queryParams["collaborator_email"] = params.user.email;

  const queryString = new URLSearchParams(queryParams).toString();

  return await performFilterByCriteria(
    `/docvault/folders/?${queryString}`,
    isPending,
  );
};

export const onFilterFolders = async (
  filterChoice: string,
  sortingChoice: string,
  filterDates: any,
  filterUser: any,
  search: string | null,
  dispatch: FolderFilterDispatch,
) => {
  const filter = filterChoice;
  const sort = sortingChoice;
  const date =
    filterDates?.startDate && filterDates?.endDate
      ? {
          startDate: dayjs(filterDates.startDate).format("YYYY-MM-DD"),
          endDate: dayjs(filterDates.endDate).format("YYYY-MM-DD"),
        }
      : null;
  const user = filterUser || null;

  dispatch(setIsLoading(true));
  try {
    const filterResult = await getFilterQueryParams({
      filter,
      sort,
      date,
      user,
      search,
    });
    dispatch(setFilterState(filterResult));
  } catch (error) {
    if (error instanceof Error) {
      console.error("Error fetching folders:", error);
      return;
    }
    throw error;
  } finally {
    dispatch(setIsLoading(false));
  }
};

interface FilterParams {
  [key: string]: any; // Allow for any properties
}

export const onFilterMyFolders = async (
  filterChoice: string,
  sortingChoice: string,
  filterDates: FilterDates | null,
  filterUser: FilterUser | null,
  searchTerm: string | null,
  dispatch: FolderFilterDispatch,
) => {
  const params: FilterParams = {};

  const savedFilter = sessionStorage.getItem("folderFilter");
  if (savedFilter && filterOptions[savedFilter]) {
    Object.assign(params, filterOptions[savedFilter]);
  }

  const savedSpecialFilter = sessionStorage.getItem("specialFilter");
  if (savedSpecialFilter && filterOptions[savedSpecialFilter]) {
    Object.assign(params, filterOptions[savedSpecialFilter]);
  }

  if (filterChoice && filterOptions[filterChoice]) {
    Object.assign(params, filterOptions[filterChoice]);
  }

  if (sortingChoice && sortOptions[sortingChoice]) {
    Object.assign(params, sortOptions[sortingChoice]);
  } else if (sortingChoice === "newest") {
    params.ordering = "-created_at";
  } else if (sortingChoice === "name_asc") {
    params.ordering = "name";
  } else if (sortingChoice === "name_desc") {
    params.ordering = "-name";
  } else if (sortingChoice === "status") {
    params.ordering = "status";
  }

  if (filterDates?.startDate && filterDates?.endDate) {
    params.created_at_after = dayjs(filterDates.startDate).format("YYYY-MM-DD");
    params.created_at_before = dayjs(filterDates.endDate).format("YYYY-MM-DD");
  }

  if (filterChoice === "user" && filterUser) {
    params.assigned_user_email = filterUser.email;
  }

  if (searchTerm) {
    params.search = searchTerm;
  }

  // Direct API call instead of Redux thunk
  dispatch(setIsLoading(true));

  // Convert params to query string
  const queryString = new URLSearchParams(params).toString();
  const url = `/docvault/folders/user/${queryString ? "?" + queryString : ""}`;

  try {
    const response = await api.get<FolderResult>(url);
    dispatch(setFilterState(response.data));
  } catch (error) {
    // Handle error
    console.error("Error fetching my folders:", error);
  } finally {
    dispatch(setIsLoading(false));
  }
};
